import { useCallback, useEffect, useState } from 'react';
import {
  notificationService,
  type AlertOutcome,
  type NotificationItem,
  type ResolutionCheckResponse,
} from '../../services/notifications';
import AudioNotesInput from './AudioNotesInput';
import styles from './AlertOutcomeDialog.module.css';

/** Mirrors MIN_REASON_LENGTH in services/notifications/alert_outcome.py. */
const MIN_REASON_LENGTH = 10;

interface AlertOutcomeDialogProps {
  alert: NotificationItem;
  onClose: () => void;
  /** Called once the alert has been cleared server-side. */
  onResolved: (alertId: string) => void;
}

/**
 * Clears a must-acknowledge audit alert by asking the one question that was
 * never captured: did you change the order, or does it not need changing?
 *
 * The dialog opens by re-deriving the order against LIVE Sage, because the
 * portal audit trail cannot answer this — a CSR who corrects an order directly
 * in Sage leaves no portal record. That verdict pre-selects the honest answer:
 * if the flagged difference is gone the order was fixed; if it is still there,
 * claiming a fix would quietly close a live error. When Sage cannot be reached
 * or the answer is genuinely ambiguous, neither option is pre-selected —
 * steering on a guess is worse than not steering.
 *
 * "No change needed" requires a reason. That reason is the only record of what
 * the alert did not know, and the only thing the extraction agent can learn
 * from; it is dictatable, because these alerts get cleared in a batch a day or
 * more later and nobody types a paragraph in that mood.
 */
export function AlertOutcomeDialog({
  alert,
  onClose,
  onResolved,
}: AlertOutcomeDialogProps) {
  const [check, setCheck] = useState<ResolutionCheckResponse | null>(null);
  const [checking, setChecking] = useState(true);
  const [outcome, setOutcome] = useState<AlertOutcome | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await notificationService.getResolutionCheck(alert.id);
        if (cancelled) return;
        setCheck(result);
        // Pre-select only what the data actually supports.
        if (result.suggested_outcome) setOutcome(result.suggested_outcome);
      } catch {
        // A failed check must not block clearing the alert — fall through to
        // the neutral, unsteered form.
        if (!cancelled) setCheck(null);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [alert.id]);

  const reasonRequired = outcome === 'no_change_needed';
  const reasonTooShort = reason.trim().length < MIN_REASON_LENGTH;
  const canSubmit =
    outcome !== null && !submitting && !(reasonRequired && reasonTooShort);

  const handleSubmit = useCallback(async () => {
    if (!outcome) return;
    setSubmitting(true);
    setError('');
    try {
      await notificationService.submitAlertOutcome(
        alert.id,
        outcome,
        reason.trim() || undefined,
      );
      onResolved(alert.id);
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'Could not save that — please try again.';
      setError(detail);
      setSubmitting(false);
    }
  }, [alert.id, outcome, reason, onResolved]);

  const contradictsFix =
    outcome === 'fixed' && check?.verdict === 'unresolved';

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="alert-outcome-title"
      onClick={onClose}
    >
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <h2 id="alert-outcome-title" className={styles.title}>
          {alert.title}
        </h2>
        <p className={styles.message}>{alert.message}</p>

        {/* What Sage says right now */}
        <div className={styles.checkRow} data-testid="alert-sage-check">
          {checking && <span className={styles.checking}>Checking Sage…</span>}
          {!checking && check?.verdict === 'unresolved' && (
            <span className={styles.unresolved}>
              ✗ Still showing in Sage
              {check.divergences.length > 0 && (
                <span className={styles.divergences}>
                  {check.divergences.join(' · ')}
                </span>
              )}
            </span>
          )}
          {!checking && check?.verdict === 'resolved' && (
            <span className={styles.resolved}>
              ✓ Sage now matches what we submitted
            </span>
          )}
          {!checking && (!check || check.verdict === 'unknown') && (
            <span className={styles.unknown}>
              Couldn’t verify against Sage
              {check?.reason ? ` — ${check.reason}` : ''}
            </span>
          )}
        </div>

        <fieldset className={styles.choices}>
          <legend className={styles.legend}>What happened?</legend>
          <button
            type="button"
            data-testid="outcome-fixed"
            className={outcome === 'fixed' ? styles.choiceSel : styles.choice}
            onClick={() => setOutcome('fixed')}
            disabled={submitting}
          >
            I fixed it
          </button>
          <button
            type="button"
            data-testid="outcome-no-change"
            className={
              outcome === 'no_change_needed' ? styles.choiceSel : styles.choice
            }
            onClick={() => setOutcome('no_change_needed')}
            disabled={submitting}
          >
            No change needed
          </button>
        </fieldset>

        {contradictsFix && (
          <p className={styles.warning} data-testid="fix-contradiction">
            Heads up — Sage still shows this difference. If you meant to correct
            it, the change hasn’t landed yet.
          </p>
        )}

        {outcome !== null && (
          <div className={styles.reasonBlock}>
            {/* The label lives on AudioNotesInput itself — it renders its own
                label row next to the mic, so a second label here would sit
                directly above it and contradict it. */}
            <AudioNotesInput
              value={reason}
              onChange={setReason}
              disabled={submitting}
              rows={3}
              label={
                reasonRequired
                  ? 'What were we missing? (required)'
                  : 'Anything worth noting? (optional)'
              }
              placeholder={
                reasonRequired
                  ? 'e.g. White Cap buys off their own vendor part number — price comes from their contract sheet, not our list.'
                  : 'Optional.'
              }
            />
            {reasonRequired && reasonTooShort && (
              <span className={styles.hint} data-testid="reason-hint">
                A sentence is enough — this is what the AI learns from.
              </span>
            )}
          </div>
        )}

        {error && (
          <p className={styles.error} role="alert" data-testid="outcome-error">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancel}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="outcome-submit"
            className={styles.submit}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? 'Saving…' : 'Submit & clear'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AlertOutcomeDialog;
